"use client";

import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { useSelector } from "react-redux";
import ChipIssueForm from "./chipIssueForm.js";
import { maskPhone } from "../../helper";

export default function ChipIssue() {
  const [records, setRecords] = useState([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 3;
  const [pagination, setPagination] = useState({});
  const [loanOnly, setLoanOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalFromLoan, setTotalFromLoan] = useState(0);
  const [searched, setSearched] = useState(false);

  const loginSlice = useSelector((state) => state.loginSlice);
  const token = loginSlice.token;

  const fetchChipIssues = async ({ loanFilter = loanOnly, from = dateFrom, to = dateTo, pg = page } = {}) => {
    try {
      let url = `${apiBaseUrl}/chip-issue?page=${pg + 1}&size=${rowsPerPage}`;
      if (loanFilter) url += `&loan_only=true`;
      if (from) url += `&date_from=${encodeURIComponent(from)}`;
      if (to)   url += `&date_to=${encodeURIComponent(to)}`;
      const res = await axiosRequest("GET", url, token);
      setRecords(res?.data?.chipIssueList || []);
      setPagination(res?.data?.paginationData || {});
      setTotalAmount(res?.data?.totalAmount || 0);
      setTotalFromLoan(res?.data?.totalFromLoan || 0);
      setSearched(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleLoanToggle = (e) => {
    const val = e.target.checked;
    setLoanOnly(val);
    setPage(0);
    fetchChipIssues({ loanFilter: val, pg: 0 });
  };

  const handleSearch = () => {
    setPage(0);
    fetchChipIssues({ pg: 0 });
  };

  const handleReset = () => {
    setDateFrom("");
    setDateTo("");
    setLoanOnly(false);
    setPage(0);
    setSearched(false);
    fetchChipIssues({ loanFilter: false, from: "", to: "", pg: 0 });
  };

  useEffect(() => {
    fetchChipIssues();
  }, [page, rowsPerPage]);

  return (
    <Box sx={{ width: "100%", marginTop: { xs: "55px", md: "95px" } }}>
      <Box p={3} sx={{ padding: { xs: "16px", md: "32px" } }}>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Chip Issue
            </Typography>
            <Typography color="text.secondary">
              Track chip issuance to players at tables
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            <FormControlLabel
              control={<Switch checked={loanOnly} onChange={handleLoanToggle} color="warning" />}
              label={<Typography fontWeight={600} color={loanOnly ? "warning.main" : "text.secondary"}>Loan Purchases Only</Typography>}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDrawer(true)}
              sx={{ borderRadius: 3, height: 45 }}
            >
              Issue Chips
            </Button>
          </Box>
        </Box>

        {/* Date filter bar */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                size="small"
                fullWidth
                label="Date & Time From"
                type="datetime-local"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                size="small"
                fullWidth
                label="Date & Time To"
                type="datetime-local"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={!dateFrom}
                inputProps={{ min: dateFrom || "" }}
              />
            </Grid>
            <Grid item xs="auto">
              <Button variant="contained" color="success" onClick={handleSearch} sx={{ height: 40 }}>Search</Button>
            </Grid>
            <Grid item xs="auto">
              <Button variant="contained" color="error" onClick={handleReset} sx={{ height: 40 }}>Reset</Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Summary strip */}
        {searched && (
          <Box display="flex" gap={3} alignItems="center" flexWrap="wrap" mb={2}
            sx={{ p: 2, borderRadius: 2, background: "linear-gradient(135deg,#1e3c72,#2a5298)", color: "#fff" }}>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Records</Typography>
              <Typography variant="h6" fontWeight={700}>{pagination.totalRecords ?? records.length}</Typography>
            </Box>
            <Box sx={{ borderLeft: "1px solid rgba(255,255,255,0.3)", pl: 3 }}>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Chip Amount</Typography>
              <Typography variant="h5" fontWeight={800}>{Number(totalAmount).toLocaleString("en-IN")}</Typography>
            </Box>
            {totalFromLoan > 0 && (
              <Box sx={{ borderLeft: "1px solid rgba(255,255,255,0.3)", pl: 3 }}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Total via Loan</Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: "#ffd54f" }}>{Number(totalFromLoan).toLocaleString("en-IN")}</Typography>
              </Box>
            )}
          </Box>
        )}

        <Paper sx={{ borderRadius: 4, overflow: "hidden", overflowX: "auto" }}>
          <Table sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Player</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Total Amount</TableCell>
                <TableCell>Deduction Breakdown</TableCell>
                <TableCell>Remarks</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {records.length > 0 ? (
                records.map((rec) => {
                  const bd = rec.deduction_breakdown || {};
                  const fromLoan = Number(bd.from_loan || 0);
                  return (
                    <TableRow key={rec._id}>
                      <TableCell>
                        {new Date(rec.date).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={600}>{rec.user_name || "—"}</Typography>
                        <Typography variant="caption" color="text.secondary">{rec.player_id}</Typography>
                      </TableCell>
                      <TableCell>{maskPhone(rec.phone) || "—"}</TableCell>
                      <TableCell>
                        <Typography fontWeight={700} color="success.main">
                          {rec.total_amount}
                        </Typography>
                        {fromLoan > 0 && (
                          <Chip label="Loan" size="small" color="warning" sx={{ mt: 0.5, fontWeight: 700 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.3}>
                          {Number(bd.from_deposit || 0) > 0 && <Typography variant="caption">Deposit: {bd.from_deposit}</Typography>}
                          {Number(bd.from_winning || 0) > 0 && <Typography variant="caption">Winning: {bd.from_winning}</Typography>}
                          {Number(bd.from_promo || 0) > 0 && <Typography variant="caption">Promo: {bd.from_promo}</Typography>}
                          {fromLoan > 0 && <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 700 }}>Loan: {fromLoan}</Typography>}
                          {!bd.from_deposit && !bd.from_winning && !bd.from_promo && !fromLoan && <Typography variant="caption" color="text.disabled">—</Typography>}
                        </Box>
                      </TableCell>
                      <TableCell>{rec.remarks || "—"}</TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No Chip Issues Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <ChipIssueForm
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          refresh={fetchChipIssues}
        />
      </Box>
    </Box>
  );
}
